// @flow

import {
  selectOneProduct,
  getCart,
  removeCartProducts,
  addCartBundle,
} from '@gandi/cart';

import type { BundleType } from '@gandi/cart';

import type { ThunkAction, GetState, Dispatch } from 'src/types';

import { getSuggestionBestPrice } from '../../../modules/actions/cart';
import { getMinDuration } from '../../../modules/helpers/suggestions';
import { selectFilterValue } from '../../../modules/selectors/filters';
import { selectPosition } from '../../../modules/selectors/results';
import {
  selectSearchValue,
  selectSearchUUID,
} from '../../../modules/selectors/search';

import type {
  ExtendedSuggestion,
  BundleGrids,
  BundleItems,
} from '../../../types';

type AddBundleToCartParams = {
  bundledSuggestions: Array<ExtendedSuggestion>,
  bundleGrids: BundleGrids,
  bundleItems: BundleItems,
  bundleType: BundleType,
  bundleUseOnce: boolean | void,
};

type AddBundleToCartReturn = Promise<void>;
type AddBundleToCart<R> = (params: AddBundleToCartParams) => R;
export type AddBundleToCartBoundAction = AddBundleToCart<AddBundleToCartReturn>;
export type AddBundleToCartThunkAction = AddBundleToCart<
  ThunkAction<AddBundleToCartReturn>,
>;

export const addBundleToCart: AddBundleToCartThunkAction =
  ({
    bundledSuggestions,
    bundleGrids,
    bundleItems,
    bundleType,
    bundleUseOnce,
  }) =>
  async (dispatch: Dispatch, getState: GetState) => {
    const toRemove = bundledSuggestions
      .filter(suggestion => suggestion.addedToCart)
      .map(({ fqdn }) => selectOneProduct(getState(), { fqdn }))
      .filter(Boolean)
      // $FlowFixMe[sketchy-number-and]
      .filter(({ id }) => id && !String(id).startsWith('optimistic'));

    if (toRemove.length) {
      try {
        // $FlowFixMe[prop-missing]
        await dispatch(removeCartProducts(toRemove));
      } catch (err) {
        await dispatch(getCart());
      }
    }

    const searchUUID = selectSearchUUID(getState());

    const tldFilter = selectFilterValue(getState(), 'tlds')
      .split(',')
      .filter(tld => tld !== 'suggested');

    const products = bundledSuggestions.map(suggestion => ({
      process: 'create',
      product_type: 'domain',
      discount_code: bundleItems.find(({ fqdn }) => fqdn === suggestion.fqdn)
        ?.code,
      payload: {
        fqdn: suggestion.fqdn,
        tld: suggestion.tld,
        tld_phase: suggestion.phase,
        corporate: suggestion.corporate,
        duration: getMinDuration(suggestion),
        shopping: {
          best_price: getSuggestionBestPrice(suggestion),
        },
      },
      metadatas: {
        corporate: suggestion.corporate,
        fqdn: suggestion.fqdn,
        query: selectSearchValue(getState()),
        position: selectPosition(getState(), suggestion.fqdn),
        selected_filter: tldFilter,
        real_position: undefined,
        lang: undefined,
        searchUUID,
      },
    }));

    try {
      // ... and products after: for performance purpose
      await dispatch(
        addCartBundle({
          products,
          type: bundleType,
          grids: bundleGrids,
          useOnce: bundleUseOnce,
        }),
      );
    } finally {
      await dispatch(getCart()).catch(() => {});
    }
  };
